import CaprockMark from '@/components/linear/caprock-mark';
import { colorFromId } from '@/lib/linear-utils';
import { companyContextPath } from '@/lib/linear-paths';
import type { SharedPageProps } from '@/types/shared';
import { router, usePage } from '@inertiajs/react';
import { Check, ChevronDown } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

export default function WorkspaceSwitcher() {
  const { companies, currentCompany } = usePage<SharedPageProps>().props;
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const name = currentCompany?.name ?? 'CAP Rock Labs';
  const color = currentCompany ? colorFromId(currentCompany.id) : '#5e6ad2';

  useEffect(() => {
    if (!open) return;

    const onPointer = (event: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', onPointer);
    return () => document.removeEventListener('mousedown', onPointer);
  }, [open]);

  const switchCompany = (companyId: string) => {
    setOpen(false);
    if (companyId === currentCompany?.id) return;
    router.patch(companyContextPath(), { companyId }, { preserveScroll: true });
  };

  return (
    <div ref={rootRef} style={{ position: 'relative' }}>
      <button
        type="button"
        className="ln-user-btn"
        onClick={() => setOpen((value) => !value)}
        aria-haspopup="listbox"
        aria-expanded={open}
        style={{ height: 32, padding: '0 4px' }}
      >
        <CaprockMark size={16} tone="on-dark" />
        <span
          style={{
            width: 16,
            height: 16,
            borderRadius: 4,
            background: color,
            flexShrink: 0,
          }}
        />
        <span
          style={{
            flex: 1,
            textAlign: 'left',
            fontSize: '0.8125rem',
            fontWeight: 510,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {name}
        </span>
        <ChevronDown size={14} color="var(--ln-muted)" />
      </button>

      {open ? (
        <div className="ln-popover" style={{ top: 36, left: 0, right: 0 }} role="listbox">
          {companies.map((company) => (
            <button
              key={company.id}
              type="button"
              className="ln-popover-item"
              data-active={company.id === currentCompany?.id ? 'true' : 'false'}
              onClick={() => switchCompany(company.id)}
            >
              <span
                style={{
                  width: 16,
                  height: 16,
                  borderRadius: 4,
                  background: colorFromId(company.id),
                  flexShrink: 0,
                }}
              />
              <span style={{ flex: 1, textAlign: 'left' }}>{company.name}</span>
              {company.id === currentCompany?.id ? <Check size={14} /> : null}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}
